/**
 * 전역 API 에러 메시지 해석기
 *
 * 츄잉 API는 두 방식으로 에러를 돌려준다:
 *   1) HTTP 200 + body.result_code(≠0) + body.result_msg   (대부분의 비즈니스 예외)
 *   2) HTTP 4xx/5xx + body.result_msg                        (ShopExceptionHandler 등)
 *
 * 어느 경우든 "사용자가 읽을 수 있는 사유"를 최우선으로 노출하고,
 * 사유가 없을 때만 HTTP 상태코드를 한글 안내로 매핑한다.
 * 절대로 axios 기본 문자열("Request failed with status code 400")을
 * 그대로 사용자에게 노출하지 않는다.
 */

// HTTP 상태코드 → 사용자 안내 문구
const HTTP_STATUS_MESSAGE: Record<number, string> = {
  400: "입력하신 내용을 다시 확인해 주세요.",
  401: "로그인이 필요합니다. 다시 로그인해 주세요.",
  403: "이 작업을 수행할 권한이 없습니다.",
  404: "요청하신 대상을 찾을 수 없습니다.",
  408: "요청 시간이 초과되었습니다. 다시 시도해 주세요.",
  409: "이미 처리되었거나 중복된 요청입니다.",
  413: "전송한 데이터의 용량이 너무 큽니다.",
  422: "입력값이 올바르지 않습니다. 다시 확인해 주세요.",
  429: "요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.",
  500: "서버에 일시적인 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.",
  502: "서버에 연결할 수 없습니다. 잠시 후 다시 시도해 주세요.",
  503: "서비스 점검 중이거나 일시적으로 이용할 수 없습니다.",
  504: "서버 응답이 지연되고 있습니다. 잠시 후 다시 시도해 주세요.",
};

const DEFAULT_MESSAGE =
  "요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.";
const NETWORK_MESSAGE = "네트워크 연결 상태를 확인해 주세요.";

/** axios/브라우저 기본 메시지(=사용자에게 의미 없는 코드 문자열)인지 판별 */
const isRawTransportMessage = (msg?: unknown): boolean => {
  if (typeof msg !== "string" || !msg.trim()) return true;
  return (
    /^Request failed with status code/i.test(msg) ||
    /^Network Error$/i.test(msg) ||
    /^timeout of /i.test(msg) ||
    /^canceled$/i.test(msg)
  );
};

/** 서버 응답 본문에서 사람이 읽을 수 있는 메시지를 추출 */
const pickServerMessage = (data: any): string | undefined => {
  if (!data || typeof data !== "object") return undefined;
  const candidate = data.result_msg ?? data.message ?? data.error;
  if (typeof candidate === "string" && candidate.trim() && !isRawTransportMessage(candidate)) {
    return candidate.trim();
  }
  return undefined;
};

export interface ResolvedError {
  /** 사용자에게 보여줄 사유 */
  message: string;
  /** 추적용 상세(상태코드/요청 경로) — 콘솔/텔레메트리 전용 */
  detail?: string;
}

/**
 * axios 에러(throw된 것) 또는 HTTP200 비즈니스 에러 객체(result_code/result_msg)를
 * 받아 사용자 친화 메시지로 변환한다.
 *
 * @param input axios error | result 객체
 * @param uri   실패한 요청 경로(있으면 추적 상세에 포함)
 */
export function resolveApiError(input: any, uri?: string): ResolvedError {
  const resp = input?.response;

  // 응답 자체가 없는 네트워크/요청 실패
  if (input && !resp && (input.code === "ERR_NETWORK" || input.message === "Network Error")) {
    return { message: NETWORK_MESSAGE, detail: uri ? `요청: ${uri}` : undefined };
  }

  // resp 가 있으면 HTTP 에러, 없으면 result 객체로 간주
  const data = resp?.data ?? input;
  const serverMsg = pickServerMessage(data);
  const status: number | undefined = resp?.status;

  // 우선순위 1) 서버가 준 사람용 메시지
  if (serverMsg) {
    return {
      message: serverMsg,
      detail:
        typeof status === "number"
          ? `오류 코드 ${status}${uri ? ` · ${uri}` : ""}`
          : uri
          ? `요청: ${uri}`
          : undefined,
    };
  }

  // 우선순위 2) HTTP 상태코드 한글 매핑
  if (typeof status === "number") {
    const mapped =
      HTTP_STATUS_MESSAGE[status] ??
      (status >= 500 ? HTTP_STATUS_MESSAGE[500] : DEFAULT_MESSAGE);
    return { message: mapped, detail: `오류 코드 ${status}${uri ? ` · ${uri}` : ""}` };
  }

  // 우선순위 3) 폴백 — raw transport 문자열은 숨기고 일반 안내로 대체
  if (isRawTransportMessage(input?.message)) {
    return { message: DEFAULT_MESSAGE, detail: uri ? `요청: ${uri}` : undefined };
  }
  return { message: String(input?.message ?? DEFAULT_MESSAGE) };
}

/**
 * 팝업에 바로 넣을 수 있는 사용자 안내 문자열.
 * 요청 경로와 HTTP 상태는 resolveApiError().detail 로만 남기고 사용자에게는
 * 노출하지 않는다. 내부 경로는 문제 해결에 도움이 되지 않고 불안만 키운다.
 */
export function formatApiError(input: any, uri?: string): string {
  return resolveApiError(input, uri).message;
}
